import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { motion, useInView } from "framer-motion";
import { 
  BookOpen, 
  Users, 
  Calendar, 
  GraduationCap, 
  FileCheck, 
  Award, 
  Clock, 
  BarChart,
  Building,
  Check,
  Star,
  CheckCircle,
  ChevronRight,
  BriefcaseBusiness,
  Brain,
  Sparkles
} from "lucide-react";

const EnterpriseTraining = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("technical");
  const headerRef = useRef(null);
  const statsRef = useRef(null);
  const isHeaderInView = useInView(headerRef, { once: true });
  const isStatsInView = useInView(statsRef, { once: true });

  const handleRequestDemo = (programType: string) => {
    toast({
      title: "Demo Requested",
      description: `We'll contact you about the ${programType} training program soon.`,
    });
  };

  const handleScheduleConsultation = () => {
    toast({
      title: "Request Sent",
      description: "Our enterprise training team will contact you soon.",
    });
  };

  const testimonials = [
    {
      quote: "The custom training programs significantly accelerated our team's digital transformation journey.",
      author: "Sarah Johnson",
      position: "CTO, Global Tech Solutions",
      company: "Global Tech Solutions"
    },
    {
      quote: "Our development team's productivity increased by 35% after completing the Full-Stack Development Bootcamp.",
      author: "Michael Chen",
      position: "Engineering Director",
      company: "Innovate Software Inc."
    },
    {
      quote: "The leadership training gave our managers the tools they needed to guide teams through complex projects.",
      author: "Emily Rodriguez",
      position: "HR Director",
      company: "Fusion Enterprises"
    }
  ];

  const outcomes = [
    { 
      stat: "94%", 
      label: "of participants apply skills immediately",
      icon: <CheckCircle className="w-8 h-8 text-emerald-500" />
    },
    { 
      stat: "86%", 
      label: "report improved team collaboration",
      icon: <Users className="w-8 h-8 text-sky-500" /> 
    },
    { 
      stat: "92%", 
      label: "satisfaction rate across all programs",
      icon: <Star className="w-8 h-8 text-amber-500" /> 
    },
    { 
      stat: "40%", 
      label: "faster project completion times",
      icon: <Clock className="w-8 h-8 text-purple-500" /> 
    }
  ];

  const trainingPrograms = {
    technical: [
      {
        id: 1,
        title: "Full-Stack Development Bootcamp",
        description: "Intensive training for development teams to master modern web technologies.",
        duration: "8 weeks",
        format: "Hybrid (online/in-person)",
        participants: "5-20",
        level: "Intermediate to Advanced",
        icon: <BookOpen className="h-5 w-5 text-sky-500" />,
        highlights: [
          "Custom curriculum based on your tech stack",
          "Hands-on project-based learning",
          "1:1 mentoring sessions",
          "Post-training support for 3 months"
        ]
      },
      {
        id: 2,
        title: "Cloud Architecture & DevOps",
        description: "Equip your IT teams with cloud-native skills and DevOps practices.",
        duration: "6 weeks",
        format: "Online with live sessions",
        participants: "5-15",
        level: "Intermediate to Advanced",
        icon: <FileCheck className="h-5 w-5 text-emerald-500" />,
        highlights: [
          "AWS/Azure/GCP tailored training",
          "CI/CD pipeline implementation",
          "Infrastructure as Code practices",
          "Security best practices"
        ]
      }
    ],
    leadership: [
      {
        id: 3,
        title: "Digital Transformation Leadership",
        description: "Prepare leaders to guide teams through digital transformation initiatives.",
        duration: "4 weeks",
        format: "Executive sessions",
        participants: "5-10",
        level: "Executive/Management",
        icon: <Users className="h-5 w-5 text-violet-500" />,
        highlights: [
          "Change management strategies",
          "Digital roadmap development",
          "Cross-functional team leadership",
          "Technology investment planning"
        ]
      },
      {
        id: 4,
        title: "Agile Management Certification",
        description: "Comprehensive training in agile methodologies for project managers and team leads.",
        duration: "5 weeks",
        format: "Hybrid (online/in-person)",
        participants: "8-12",
        level: "Management",
        icon: <Calendar className="h-5 w-5 text-amber-500" />,
        highlights: [
          "Scrum and Kanban methodologies",
          "Agile transformation strategies",
          "Certification preparation",
          "Practical implementation workshops"
        ]
      }
    ],
    onboarding: [
      {
        id: 5,
        title: "Enterprise Platform Onboarding",
        description: "Get your teams up to speed with your enterprise tools and platforms.",
        duration: "3 weeks",
        format: "Online modules with live support",
        participants: "10-50",
        level: "All levels",
        icon: <GraduationCap className="h-5 w-5 text-blue-500" />,
        highlights: [
          "Customized to your specific platforms",
          "Role-based learning paths",
          "Interactive assessments",
          "Reference materials and guides"
        ]
      },
      {
        id: 6,
        title: "Remote Workforce Enablement",
        description: "Comprehensive program to optimize remote and hybrid team productivity.",
        duration: "4 weeks",
        format: "Fully remote",
        participants: "Unlimited",
        level: "All levels",
        icon: <Clock className="h-5 w-5 text-rose-500" />,
        highlights: [
          "Digital collaboration tools training",
          "Remote productivity best practices",
          "Communication protocols",
          "Work-life balance strategies"
        ]
      }
    ],
    compliance: [
      {
        id: 7,
        title: "Industry Compliance Certification",
        description: "Ensure your teams understand and follow industry-specific compliance requirements.",
        duration: "6 weeks",
        format: "Self-paced with assessments",
        participants: "Unlimited",
        level: "All employees",
        icon: <Award className="h-5 w-5 text-amber-500" />,
        highlights: [
          "Industry-specific regulations coverage",
          "Case studies and scenario training",
          "Certification preparation",
          "Regular compliance updates"
        ]
      },
      {
        id: 8,
        title: "Data Security & Privacy Training",
        description: "Comprehensive training on data protection, privacy laws, and security practices.",
        duration: "4 weeks",
        format: "Online modules with assessments",
        participants: "Unlimited",
        level: "All employees",
        icon: <BarChart className="h-5 w-5 text-emerald-500" />,
        highlights: [
          "GDPR, CCPA, and other regulations",
          "Security best practices",
          "Data breach response procedures",
          "Role-specific privacy protocols"
        ]
      }
    ]
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white">
      <div className="bg-gradient-to-r from-indigo-50 via-blue-50 to-sky-50 border-b border-gray-100">
        <div className="container mx-auto px-4 py-16">
          <motion.div 
            ref={headerRef}
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-block p-2 bg-white shadow-sm rounded-xl mb-6">
              <Badge className="px-3 py-1 bg-indigo-500 hover:bg-indigo-500 text-white">
                Enterprise Solutions
              </Badge>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 leading-tight">
              Custom Training Solutions for <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Enterprise Teams</span>
            </h1>
            
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              Accelerate your team's growth with tailored training programs designed to build the specific skills your organization needs to thrive in today's competitive landscape.
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center">
              <Badge variant="outline" className="px-4 py-2 text-sm bg-white">
                <GraduationCap className="w-4 h-4 mr-2" />
                Customized Curricula
              </Badge>
              <Badge variant="outline" className="px-4 py-2 text-sm bg-white">
                <Users className="w-4 h-4 mr-2" />
                Expert Trainers
              </Badge>
              <Badge variant="outline" className="px-4 py-2 text-sm bg-white">
                <Award className="w-4 h-4 mr-2" />
                Certification Preparation
              </Badge>
              <Badge variant="outline" className="px-4 py-2 text-sm bg-white">
                <BarChart className="w-4 h-4 mr-2" />
                Progress Tracking
              </Badge>
            </div>
          </motion.div>
        </div>
      </div>
      
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <motion.div 
            ref={statsRef}
            initial={{ opacity: 0 }}
            animate={isStatsInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold text-center mb-12">
              Measurable <span className="text-indigo-600">Outcomes</span> from Our Training Programs
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {outcomes.map((outcome, index) => (
                <div key={index} className="text-center">
                  <div className="mx-auto w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                    {outcome.icon}
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">{outcome.stat}</h3>
                  <p className="text-gray-600">{outcome.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
      
      <div className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Enterprise Training Programs</h2>
            <p className="text-gray-600">
              Select from our catalog of proven training programs or work with us to create a custom solution tailored to your organization's unique needs.
            </p>
          </div>
          
          <Tabs defaultValue="technical" value={activeTab} onValueChange={setActiveTab} className="mb-16">
            <TabsList className="w-full max-w-2xl mx-auto grid grid-cols-2 md:grid-cols-4 mb-8">
              <TabsTrigger value="technical" className="data-[state=active]:bg-sky-50">
                <BookOpen className="w-4 h-4 mr-2" />
                Technical
              </TabsTrigger>
              <TabsTrigger value="leadership" className="data-[state=active]:bg-violet-50">
                <BriefcaseBusiness className="w-4 h-4 mr-2" />
                Leadership
              </TabsTrigger>
              <TabsTrigger value="onboarding" className="data-[state=active]:bg-blue-50">
                <Users className="w-4 h-4 mr-2" />
                Onboarding
              </TabsTrigger>
              <TabsTrigger value="compliance" className="data-[state=active]:bg-amber-50">
                <FileCheck className="w-4 h-4 mr-2" />
                Compliance
              </TabsTrigger>
            </TabsList>
            
            {Object.entries(trainingPrograms).map(([category, programs]) => (
              <TabsContent key={category} value={category}>
                <motion.div
                  variants={container}
                  initial="hidden"
                  animate={activeTab === category ? "show" : "hidden"}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  {programs.map((program) => (
                    <motion.div key={program.id} variants={item}>
                      <Card className="h-full hover:shadow-md transition-shadow border-t-4 border-t-indigo-500">
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="flex items-center">
                              <div className="p-2 rounded-full bg-gray-50 mr-3">
                                {program.icon}
                              </div>
                              <CardTitle>{program.title}</CardTitle>
                            </div>
                          </div>
                          <CardDescription className="mt-2">{program.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <p className="text-sm font-medium text-gray-500">Duration</p>
                              <p className="text-sm">{program.duration}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">Format</p>
                              <p className="text-sm">{program.format}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">Group Size</p>
                              <p className="text-sm">{program.participants}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500">Level</p>
                              <p className="text-sm">{program.level}</p>
                            </div>
                          </div>
                          
                          <div className="mt-4">
                            <h4 className="text-sm font-medium text-gray-500 mb-2">Program Highlights</h4>
                            <ul className="space-y-2">
                              {program.highlights.map((highlight, idx) => (
                                <li key={idx} className="flex items-start">
                                  <Check className="h-4 w-4 text-emerald-500 mt-0.5 mr-2" />
                                  <span className="text-sm">{highlight}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </CardContent>
                        <CardFooter>
                          <Button 
                            onClick={() => handleRequestDemo(program.title)}
                            className="w-full bg-indigo-600 hover:bg-indigo-700"
                          >
                            Request Program Details
                          </Button>
                        </CardFooter>
                      </Card>
                    </motion.div>
                  ))}
                </motion.div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
      
      <div className="py-16 bg-indigo-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">What Our Clients Say</h2>
            <p className="text-gray-600">
              Hear from organizations that have transformed their teams through our enterprise training programs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="italic text-gray-700 mb-6">"{testimonial.quote}"</p>
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">
                      {testimonial.author.charAt(0)}
                    </div>
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">{testimonial.author}</p>
                      <p className="text-sm text-gray-500">{testimonial.position}, {testimonial.company}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
      
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Training Process</h2>
            <p className="text-gray-600">
              We follow a structured approach to ensure your team gets the maximum benefit from our training programs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[
              {
                step: "1",
                title: "Needs Assessment",
                description: "We work with you to understand your team's current skills and identify knowledge gaps.",
                icon: <Brain className="h-8 w-8 text-indigo-500" />
              },
              {
                step: "2",
                title: "Custom Curriculum",
                description: "Our experts design a tailored curriculum to address your specific requirements.",
                icon: <BookOpen className="h-8 w-8 text-indigo-500" />
              },
              {
                step: "3",
                title: "Engaging Delivery",
                description: "Interactive training sessions delivered by industry experts in your preferred format.",
                icon: <Users className="h-8 w-8 text-indigo-500" />
              },
              {
                step: "4",
                title: "Ongoing Support",
                description: "Post-training resources and support to ensure long-term skill retention.",
                icon: <Calendar className="h-8 w-8 text-indigo-500" />
              }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative">
                  <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {step.icon}
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-indigo-100 -z-10">
                      <ChevronRight className="absolute right-0 top-1/2 -translate-y-1/2 text-indigo-200" />
                    </div>
                  )}
                </div>
                <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="py-16 bg-gradient-to-r from-indigo-600 to-blue-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Elevate Your Team's Skills?</h2>
            <p className="text-indigo-100 mb-8">
              Schedule a consultation with our training experts to discuss your organization's specific needs and objectives.
            </p>
            <Button 
              size="lg" 
              onClick={handleScheduleConsultation}
              className="bg-white text-indigo-700 hover:bg-indigo-50"
            >
              <Calendar className="mr-2 h-5 w-5" />
              Schedule a Consultation
            </Button>
            
            <div className="mt-8 flex flex-wrap justify-center gap-8">
              <div className="flex items-center">
                <Sparkles className="h-5 w-5 mr-2 text-indigo-200" />
                <span className="text-indigo-100">100% Satisfaction Guarantee</span>
              </div>
              <div className="flex items-center">
                <Building className="h-5 w-5 mr-2 text-indigo-200" />
                <span className="text-indigo-100">Trusted by 500+ Organizations</span>
              </div>
              <div className="flex items-center">
                <Award className="h-5 w-5 mr-2 text-indigo-200" />
                <span className="text-indigo-100">Industry-Leading Experts</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseTraining;
